/**
 * Vuetify Plugin Configuration
 *
 * Sistema de diseño basado en la identidad visual de UNMSM
 * Migrado desde configuración Tailwind CSS
 *
 * Estructura modular:
 * - vuetify/themes.ts - Definición de temas light y dark
 * - vuetify/aliases.ts - Componentes virtuales con variantes predefinidas
 * - vuetify/defaults/ - Configuración de defaults por categoría
 */

// Vuetify
import 'vuetify/styles'
import { createVuetify } from 'vuetify'
import * as components from 'vuetify/components'
import * as directives from 'vuetify/directives'

// Labs components
import { VDateInput } from 'vuetify/labs/VDateInput'

// Date adapter
import { es } from 'vuetify/locale'

// Styles
import '@mdi/font/css/materialdesignicons.css'

// Configuration
import { unmsm, unmsmDark } from './vuetify/themes'
import { aliases } from './vuetify/aliases'
import { defaults } from './vuetify/defaults'

/**
 * Configuración de Vuetify
 *
 * ============================================
 * USO DE COLORES EN COMPONENTES
 * ============================================
 * - color="primary" → Rojo institucional (#d94c4c)
 * - color="accent" o color="secondary" → Dorado académico (#eab12b)
 * - color="success" → Verde éxito (#22c55e)
 * - color="error" o color="danger" → Rojo error (#ef4444)
 * - color="warning" → Naranja advertencia (#f59e0b)
 * - color="info" → Azul informativo (#3b82f6)
 *
 * Variaciones de tonalidad:
 * - color="primary-lighten-1" → Tono más claro (+1 a +5)
 * - color="primary-darken-1" → Tono más oscuro (+1 a +5)
 * - Disponible para todos los colores
 *
 * ============================================
 * ALIASES DE COMPONENTES
 * ============================================
 * Componentes virtuales con estilos predefinidos:
 *
 * Botones:
 * - <v-btn-primary> → Botón primario elevado (rojo)
 * - <v-btn-secondary> → Botón secundario outlined (dorado)
 * - <v-btn-tertiary> → Botón terciario text (gris)
 * - <v-btn-success> → Botón de éxito (verde)
 * - <v-btn-danger> → Botón de peligro (rojo)
 * - <v-btn-warning> → Botón de advertencia (naranja)
 * - <v-btn-info> → Botón informativo (azul)
 *
 * Cards:
 * - <v-card-elevated> → Card con elevación alta
 * - <v-card-outlined> → Card con borde
 * - <v-card-flat> → Card sin elevación
 *
 * Ejemplo de uso:
 * ```vue
 * <v-btn-primary>Guardar</v-btn-primary>
 * <v-btn-secondary>Cancelar</v-btn-secondary>
 * <v-card-elevated>
 *   <v-card-title>Título</v-card-title>
 *   <v-card-text>Contenido</v-card-text>
 * </v-card-elevated>
 * ```
 *
 * ============================================
 * DEFAULTS CONTEXTUALES
 * ============================================
 * Los componentes se comportan diferente según su contexto:
 * - Botones dentro de <v-card>: variant="text"
 * - Botones dentro de <v-dialog>: variant="elevated"
 * - Botones dentro de <v-toolbar>: variant="text"
 * - Botones dentro de <v-data-table>: variant="text", size="small"
 *
 * ============================================
 * CLASES CSS DISPONIBLES
 * ============================================
 * - .bg-primary, .bg-accent, .bg-gray-500, etc.
 * - .text-primary, .text-accent, .text-gray-900, etc.
 * - .border-primary, .border-accent, etc.
 *
 * ============================================
 * VARIABLES CSS
 * ============================================
 * - rgb(var(--v-theme-primary))
 * - rgb(var(--v-theme-accent))
 * - rgb(var(--v-theme-gray-900))
 * - var(--color-primary-500)
 * - var(--color-accent-500)
 *
 * Para transparencias:
 * - rgb(var(--v-theme-primary) / 0.5)
 * - rgb(var(--color-primary-500-rgb) / 0.3)
 */
export const vuetify = createVuetify({
  components: { ...components, VDateInput },
  directives,

  theme: {
    defaultTheme: 'unmsm',
    themes: {
      unmsm,
      unmsmDark,
    },
  },

  icons: {
    defaultSet: 'mdi',
  },

  locale: {
    locale: 'es',
    messages: { es },
  },

  aliases,
  defaults,
})
